import {AfterViewInit, Directive, ElementRef, EventEmitter, Inject, Input, NgZone, OnDestroy, Output, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';

export type HomeTabSwipeDirection = 'left' | 'right';
export const HOME_TAB_SWIPE_SETTLE_DURATION_MS = 180;
export interface HomeTabSwipePreview {
  direction: HomeTabSwipeDirection;
  /** Number of adjacent panels that must be rendered for the current drag. */
  visibleTabs: number;
  /** Panels whose thumbnails may be activated because they can reach the viewport. */
  exposedTabs?: number;
  offsetPx: number;
}

export interface HomeTabSwipeCommit {
  direction: HomeTabSwipeDirection;
  steps: number;
}

/**
 * Detects horizontal gestures without taking over taps or vertical scrolling.
 * Media galleries and nested swipe controls keep their own gesture handling.
 */
@Directive({
  selector: '[appHomeTabSwipe]'
})
export class HomeTabSwipeDirective implements AfterViewInit, OnDestroy {
  @Input() homeTabSwipePreviousCount = 0;
  @Input() homeTabSwipeNextCount = 0;
  @Output() homeTabSwipe = new EventEmitter<HomeTabSwipeCommit>();
  @Output() homeTabSwipePreview = new EventEmitter<HomeTabSwipePreview | null>();
  @Output() homeTabSwipeSettled = new EventEmitter<void>();

  private readonly isBrowser: boolean;
  private readonly swipeThresholdPx = 32;
  public readonly settleDurationMs = HOME_TAB_SWIPE_SETTLE_DURATION_MS;
  private readonly previewThresholdPx = 12;
  private readonly horizontalDominance = 1.1;
  private readonly browserBackEdgePx = 24;
  private activePointerId: number | null = null;
  private startX = 0;
  private startY = 0;
  private isDragging = false;
  private preview: HomeTabSwipePreview | null = null;
  private suppressClickUntil = 0;
  private removeListeners: Array<() => void> = [];
  private isSettling = false;
  private settleFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private navResizeObserver: ResizeObserver | null = null;
  private navHeightFrameId: number | null = null;

  private readonly ignoredTargetSelector = [
    'button',
    'input',
    'textarea',
    'select',
    '[contenteditable="true"]',
    '[contenteditable=""]',
    '[role="textbox"]',
    '[role="button"]',
    '.p-button',
    '[role="slider"]',
    '.swiper',
    '.plyr__controls',
    '.plyr__control',
    '.plyr__menu',
    'audio',
    '.overlay-gallery',
    '.overlay-gallery-modal',
    '.p-dialog',
    '.p-sidebar',
    '[role="dialog"]',
    'app-bottom-sheet',
    'app-social-input',
    '.timeline-composer-trigger',
    '[data-home-tab-swipe-ignore]',
  ].join(',');

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || typeof PointerEvent === 'undefined') {
      return;
    }

    const element = this.host.nativeElement;
    this.zone.runOutsideAngular(() => {
      const onPointerDown = (event: PointerEvent) => this.handlePointerDown(event);
      const onPointerMove = (event: PointerEvent) => this.handlePointerMove(event);
      const onPointerUp = (event: PointerEvent) => this.handlePointerUp(event);
      const onPointerCancel = (event: PointerEvent) => {
        if (event.pointerId === this.activePointerId
          && (event.type !== 'lostpointercapture' || event.target === element)) {
          this.resetGesture();
        }
      };
      const onDragStart = (event: DragEvent) => this.handleDragStart(event);
      const onWindowBlur = () => {
        if (this.activePointerId !== null) {
          this.resetGesture();
        }
      };
      const onClickCapture = (event: MouseEvent) => this.handleClickCapture(event);
      const onTransitionEnd = (event: TransitionEvent) => this.handleTransitionEnd(event);

      element.addEventListener('pointerdown', onPointerDown, {capture: true, passive: true});
      element.addEventListener('pointermove', onPointerMove, {capture: true, passive: false});
      window.addEventListener('pointerup', onPointerUp, {capture: true, passive: true});
      window.addEventListener('pointercancel', onPointerCancel, {capture: true, passive: true});
      window.addEventListener('blur', onWindowBlur);
      element.addEventListener('lostpointercapture', onPointerCancel);
      element.addEventListener('dragstart', onDragStart, true);
      element.addEventListener('click', onClickCapture, true);
      element.addEventListener('transitionend', onTransitionEnd);
      this.removeListeners = [
        () => element.removeEventListener('pointerdown', onPointerDown, true),
        () => element.removeEventListener('pointermove', onPointerMove, true),
        () => window.removeEventListener('pointerup', onPointerUp, true),
        () => window.removeEventListener('pointercancel', onPointerCancel, true),
        () => window.removeEventListener('blur', onWindowBlur),
        () => element.removeEventListener('lostpointercapture', onPointerCancel),
        () => element.removeEventListener('dragstart', onDragStart, true),
        () => element.removeEventListener('click', onClickCapture, true),
        () => element.removeEventListener('transitionend', onTransitionEnd),
      ];

      const onResize = () => this.scheduleNavHeightUpdate();
      const nav = element.querySelector<HTMLElement>('.home-content-tabs');
      if (nav && typeof ResizeObserver !== 'undefined') {
        this.navResizeObserver = new ResizeObserver(onResize);
        this.navResizeObserver.observe(nav);
      }
      window.addEventListener('resize', onResize, {passive: true});
      window.visualViewport?.addEventListener('resize', onResize, {passive: true});
      this.removeListeners.push(
        () => window.removeEventListener('resize', onResize),
        () => window.visualViewport?.removeEventListener('resize', onResize),
      );
      this.scheduleNavHeightUpdate();
    });
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach((remove) => remove());
    this.removeListeners = [];
    this.navResizeObserver?.disconnect();
    if (this.navHeightFrameId !== null) {
      cancelAnimationFrame(this.navHeightFrameId);
      this.navHeightFrameId = null;
    }
    this.resetGesture();
  }

  /** Clears a completed Home handoff after its preview panel was promoted. */
  public completeSwipeHandoff(): void {
    this.resetVisualState();
    // Home has already retained the promoted panel. Do not emit a null preview
    // here, because that would rebuild the host panel; only clear the
    // directive's equality cache so the next same-direction swipe emits.
    this.preview = null;
  }

  public get prefersReducedMotion(): boolean {
    return this.isBrowser && (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false);
  }

  private handlePointerDown(event: PointerEvent): void {
    if (this.isSettling || this.activePointerId !== null || !this.isEligiblePointer(event) || this.shouldIgnoreTarget(event.target)) {
      return;
    }
    // Preserve the operating system/browser back gesture on the left edge.
    if (event.pointerType !== 'mouse' && event.clientX <= this.browserBackEdgePx) {
      return;
    }

    this.activePointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
  }

  private handlePointerMove(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }
    if (event.pointerType === 'mouse' && event.buttons !== 1) {
      this.resetGesture();
      return;
    }

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    if (!this.isDragging) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < this.previewThresholdPx) {
        return;
      }
      // Once vertical scrolling wins, do not turn its later sideways drift
      // into a tab change.
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        this.resetGesture();
        return;
      }
      if (Math.abs(deltaX) < Math.abs(deltaY) * this.horizontalDominance) {
        return;
      }
      this.isDragging = true;
      this.host.nativeElement.classList.add('home-content-swipe-surface--dragging');
      try {
        this.host.nativeElement.setPointerCapture?.(event.pointerId);
      } catch {
        // Pointer capture is only an aid for completing a gesture outside host.
      }
    }
    if (event.cancelable) {
      event.preventDefault();
    }

    const direction: HomeTabSwipeDirection = deltaX < 0 ? 'left' : 'right';
    const availableTabs = this.availableTabs(direction);
    if (availableTabs === 0) {
      this.host.nativeElement.style.setProperty('--home-tab-swipe-offset', '0px');
      this.setPreview(null);
      return;
    }

    const maxDistance = availableTabs * this.hostWidth;
    const offset = Math.sign(deltaX) * Math.min(Math.abs(deltaX), maxDistance);
    this.host.nativeElement.style.setProperty('--home-tab-swipe-offset', `${offset}px`);
    if (Math.abs(offset) >= this.previewThresholdPx) {
      this.setPreview({
        direction,
        // Render the whole reachable track immediately. Panels themselves are
        // lightweight preview-mode instances, so a long drag never exposes a
        // blank slot while crossing an intermediate tab.
        visibleTabs: availableTabs,
        exposedTabs: Math.min(availableTabs, Math.max(1, Math.ceil(Math.abs(offset) / this.hostWidth))),
        offsetPx: offset,
      });
    }
  }

  private handlePointerUp(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    const wasDragging = this.isDragging;
    if (wasDragging) {
      this.suppressClickUntil = Date.now() + 350;
    }
    this.releasePointer();

    if (
      !wasDragging
      || Math.abs(deltaX) < this.swipeThresholdPx
      || Math.abs(deltaX) < Math.abs(deltaY) * this.horizontalDominance
    ) {
      this.resetVisualState();
      this.setPreview(null);
      return;
    }

    const direction: HomeTabSwipeDirection = deltaX < 0 ? 'left' : 'right';
    const availableTabs = this.availableTabs(direction);
    if (availableTabs === 0) {
      this.resetVisualState();
      this.setPreview(null);
      return;
    }
    const travelledDistance = Math.min(Math.abs(deltaX), availableTabs * this.hostWidth);
    const steps = Math.min(availableTabs, Math.max(1, Math.round(travelledDistance / this.hostWidth)));
    this.settleTo(direction, steps);
    this.zone.run(() => this.homeTabSwipe.emit({direction, steps}));
  }

  private isEligiblePointer(event: PointerEvent): boolean {
    return event.isPrimary
      && (event.pointerType === 'touch' || event.pointerType === 'pen'
        || (event.pointerType === 'mouse' && event.button === 0));
  }

  private shouldIgnoreTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    if (target.closest(this.ignoredTargetSelector)) {
      return true;
    }
    const player = target.closest('.plyr, video');
    if (!player) {
      return false;
    }
    const inlineVideo = player.closest('.post-media .gallery-video-container');
    // Only the inline picture is a tab swipe surface. Controls and modal
    // galleries keep their own interactions, including native video controls.
    return !inlineVideo || !!inlineVideo.querySelector('video[controls]');
  }

  private handleDragStart(event: DragEvent): void {
    if (this.activePointerId !== null && !this.shouldIgnoreTarget(event.target)
      && event.target instanceof Element && event.target.closest('img, a')) {
      event.preventDefault();
    }
  }

  private handleClickCapture(event: MouseEvent): void {
    if (Date.now() > this.suppressClickUntil) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  private resetGesture(): void {
    if (this.isDragging) {
      this.suppressClickUntil = Date.now() + 350;
    }
    this.releasePointer();
    this.resetVisualState();
    this.setPreview(null);
  }

  private releasePointer(): void {
    const pointerId = this.activePointerId;
    const wasDragging = this.isDragging;
    this.activePointerId = null;
    if (pointerId !== null && wasDragging) {
      try {
        this.host.nativeElement.releasePointerCapture?.(pointerId);
      } catch {
        // The pointer may already have been released by the browser.
      }
    }
    this.startX = 0;
    this.startY = 0;
    this.isDragging = false;
  }

  private resetVisualState(): void {
    this.clearFallbackTimers();
    this.isSettling = false;
    this.host.nativeElement.classList.remove('home-content-swipe-surface--dragging');
    this.host.nativeElement.classList.remove('home-content-swipe-surface--settling');
    this.host.nativeElement.style.setProperty('--home-tab-swipe-offset', '0px');
  }

  private settleTo(direction: HomeTabSwipeDirection, steps: number): void {
    const targetOffset = (direction === 'left' ? -1 : 1) * steps * this.hostWidth;
    this.host.nativeElement.classList.remove('home-content-swipe-surface--dragging');
    this.isSettling = true;
    this.host.nativeElement.classList.add('home-content-swipe-surface--settling');
    this.host.nativeElement.style.setProperty('--home-tab-swipe-offset', `${targetOffset}px`);
    if (this.prefersReducedMotion) {
      this.settleFallbackTimer = setTimeout(() => this.finishSettling());
      return;
    }
    this.settleFallbackTimer = setTimeout(
      () => this.finishSettling(),
      HOME_TAB_SWIPE_SETTLE_DURATION_MS,
    );
  }

  private handleTransitionEnd(event: TransitionEvent): void {
    if (event.target === this.host.nativeElement) {
      return;
    }
    if (event.propertyName === 'transform' && this.isSettling) {
      this.finishSettling();
      return;
    }
  }

  private finishSettling(): void {
    if (!this.isSettling) {
      return;
    }
    this.isSettling = false;
    if (this.settleFallbackTimer !== null) {
      clearTimeout(this.settleFallbackTimer);
      this.settleFallbackTimer = null;
    }
    this.host.nativeElement.classList.remove('home-content-swipe-surface--settling');
    this.zone.run(() => this.homeTabSwipeSettled.emit());
  }

  private clearFallbackTimers(): void {
    if (this.settleFallbackTimer !== null) {
      clearTimeout(this.settleFallbackTimer);
      this.settleFallbackTimer = null;
    }
  }

  private scheduleNavHeightUpdate(): void {
    if (this.navHeightFrameId !== null) {
      return;
    }
    this.navHeightFrameId = requestAnimationFrame(() => {
      this.navHeightFrameId = null;
      const height = this.host.nativeElement.querySelector<HTMLElement>('.home-content-tabs')?.getBoundingClientRect().height;
      if (height) {
        this.host.nativeElement.style.setProperty('--home-tab-nav-height', `${Math.ceil(height)}px`);
      }
    });
  }

  private get hostWidth(): number {
    return Math.max(1, this.host.nativeElement.getBoundingClientRect().width);
  }

  private availableTabs(direction: HomeTabSwipeDirection): number {
    return Math.max(0, direction === 'left' ? this.homeTabSwipeNextCount : this.homeTabSwipePreviousCount);
  }

  private setPreview(preview: HomeTabSwipePreview | null): void {
    if (
      preview?.direction === this.preview?.direction
      && preview?.visibleTabs === this.preview?.visibleTabs
      && preview?.exposedTabs === this.preview?.exposedTabs
    ) {
      this.preview = preview;
      return;
    }
    this.preview = preview;
    this.zone.run(() => this.homeTabSwipePreview.emit(preview));
  }
}
